import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import AdvanceNoticeBanner from "../components/AdvanceNoticeBanner";
import { API_URL, API_ORIGIN } from "../config";

const PRODUCTS_PER_PAGE = 9;

function ProductCard({ product }) {
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Normalizamos el link de la imagen
  const imageUrl = product.image?.startsWith('/') 
    ? `${API_ORIGIN}${product.image}` 
    : product.image;

  const [variant, setVariant] = useState(product.variants?.[0]?.key || "default");
  const current = product.variants?.find(v => v.key === variant) || product.variants?.[0];
  const finalPrice = current?.price_base ?? 0;

  function handleAdd() {
    if (finalPrice === 0) return;
    addToCart({
      id: `${product.id}-${variant}`,
      productId: product.id,
      name: product.name,
      detail: product.detail,
      image: imageUrl,
      currency: product.currency || "ARS",
      price: finalPrice,
      variant,
    });
  }

  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-neutral-100 flex flex-col p-4 hover:shadow-2xl transition-all group">
      <div className="relative aspect-square mb-4 rounded-2xl overflow-hidden bg-pink-50 flex items-center justify-center">
        {loading && <div className="absolute inset-0 animate-pulse bg-neutral-200"></div>}
        {imageError ? (
          <div className="flex flex-col items-center gap-2 p-4 text-center">
             <span className="text-3xl opacity-30">🍰</span>
             <p className="text-[10px] text-gray-400 font-medium">No se pudo cargar la imagen desde el proxy.</p>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${loading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => setLoading(false)}
            onError={() => { setImageError(true); setLoading(false); }}
          />
        )}
      </div>

      <div className="flex-1">
        <h3 className="text-xl font-black text-gray-800 tracking-tight">{product.name}</h3>
        <p className="text-sm text-gray-500 my-2 line-clamp-2 leading-relaxed">{product.detail}</p>
        
        {product.variants?.length > 1 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Tamaño:</span>
            <select 
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              className="text-xs font-bold bg-neutral-100 border-none rounded-full px-3 py-1 outline-none"
            >
              {product.variants.map(v => <option key={v.key} value={v.key}>{v.key} cm</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <span className="text-2xl font-black text-pink-600">${finalPrice.toLocaleString('es-AR')}</span>
        <button 
          onClick={handleAdd}
          className="bg-pink-600 text-white font-bold px-6 py-2 rounded-2xl shadow-lg shadow-pink-200 hover:bg-pink-700 active:scale-95 transition-all text-sm"
        >
          Agregar
        </button>
      </div>
    </div>
  );
}

export default function Productos() {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetch(`${API_URL}/products.php`)
      .then(r => r.json())
      .then(d => { setProducts(d); setLoading(false); })
      .catch(e => { setError("Error al conectar con la base de datos."); setLoading(false); });
  }, []);

  const totalPages = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
  const pageProducts = products.slice((page - 1) * PRODUCTS_PER_PAGE, page * PRODUCTS_PER_PAGE);

  function goToPage(next) {
    const clamped = Math.min(Math.max(next, 1), totalPages);
    setPage(clamped);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (loading) return <div className="text-center py-20 font-bold opacity-30 animate-pulse">Cargando catálogo...</div>;

  return (
    <div className="min-h-screen bg-gray-50/50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">Nuestras Tortas</h1>
        </div>

        <AdvanceNoticeBanner />

        {error && <div className="bg-red-50 text-red-600 p-6 rounded-3xl text-center font-bold mb-8 border border-red-100">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {pageProducts.map(p => <ProductCard key={p.id} product={p} />)}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className="p-2 rounded-full bg-white shadow-md text-gray-600 hover:bg-pink-50 hover:text-pink-600 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 transition-colors"
              aria-label="Página anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                onClick={() => goToPage(n)}
                className={`w-10 h-10 rounded-full font-bold text-sm transition-colors ${
                  n === page
                    ? 'bg-pink-600 text-white shadow-lg shadow-pink-200'
                    : 'bg-white text-gray-600 shadow-md hover:bg-pink-50 hover:text-pink-600'
                }`}
              >
                {n}
              </button>
            ))}

            <button
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
              className="p-2 rounded-full bg-white shadow-md text-gray-600 hover:bg-pink-50 hover:text-pink-600 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 transition-colors"
              aria-label="Página siguiente"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
